import { useState, useMemo, useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import { usePermissions } from "hooks/usePermissions";
import { useIntegrations } from "hooks/useIntegrations";
import defaultIntegrationIcon from "assets/images/integration.svg";
import { useAuth0 } from "@auth0/auth0-react";
import DataTable from "cxfcomponents/Tables/DataTable";
import SoftTypography from "components/SoftTypography";
import { getLocalDateTimeString, formatDateAndTime, allowAction } from "helpers/common";
import { PERMISSIONS } from "constants/portal";
import { UpdateIntegrationDialog } from "cxfcomponents/Integrations/UpdateIntegrationDialog";
import { Link } from "react-router-dom";
import { getResourceDependenciesWithHop } from "api/ResourceDependenciesApi";

export const Integrations = ({ connector }) => {
  const [searchName, setSearchName] = useState("");
  const { integrations, sharedIntegrations } = useIntegrations();
  const { userPermissions } = usePermissions();
  const { user } = useAuth0();
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showUpdateIntegrationDialog, setShowUpdateIntegrationDialog] = useState(false);
  const [connectorIntegrationIds, setConnectorIntegrationIds] = useState([]);

  const filterIntegrationBySearch = (integration) => {
    if (!searchName) return true;

    return integration.integration_name.toLowerCase().includes(searchName);
  };

  const filterIntegrations = (integration) => {
    const viewAllIntegrationsPermission = userPermissions.find(
      (p) => p.name === "View all integrations"
    )?.value;

    const integrationsShared = sharedIntegrations
      .filter((si) => si.integration_id === integration?.id)
      .map((si) => si.shared_user_id);

    if (viewAllIntegrationsPermission) {
      return true;
    } else {
      return (
        integration?.create_user_id === user?.cxfUserId ||
        integrationsShared.includes(user?.cxfUserId)
      );
    }
  };

  const handleCloseIntegrationDialog = () => {
    setShowUpdateIntegrationDialog(false);

    setTimeout(() => {
      setSelectedIntegration(null);
    }, 500);
  };

  const tableData = useMemo(() => {
    const connectorIntegrations = integrations.filter((integration) =>
      connectorIntegrationIds.includes(integration.id)
    );

    return {
      columns: [
        {
          Header: "icon",
          accessor: "icon",
          canGroupBy: false,
          disableSortBy: true,
          paddingLeft: "6rem",
          Cell: ({ value }) => (
            <SoftBox
              sx={{
                width: "60px",
                marginLeft: "4.5rem",
              }}
            >
              {value}
            </SoftBox>
          ),
        },
        {
          Header: "name",
          accessor: "integration_name",
          width: 200,
          canGroupBy: false,
          Cell: ({ value, row }) => (
            <SoftTypography className="white-to-dark" fontSize="14px" fontWeight="bold">
              {allowAction(
                undefined,
                undefined,
                userPermissions,
                PERMISSIONS.EDIT_ANY_INTEGRATIONS
              ) ? (
                <>
                  <SoftTypography fontSize="14px" fontWeight="normal">
                    <Link
                      style={{ color: "#2D3748" }}
                      className="white-to-dark"
                      to={`/integration/${row.original.id}`}
                    >
                      {value}
                    </Link>
                  </SoftTypography>
                </>
              ) : (
                value
              )}
            </SoftTypography>
          ),
        },
        {
          Header: "date added",
          accessor: "created_date_time",
          width: 170,
          canGroupBy: false,
          disableSortBy: false,
          defaultSortOrder: "desc",
          sortType: (rowA, rowB, columnId) => {
            const dateA = new Date(rowA.values[columnId]);
            const dateB = new Date(rowB.values[columnId]);
            return dateA - dateB;
          },
        },
        {
          accessor: "id",
          show: false,
        },
      ].map((column) => ({ ...column, disableResize: true })),
      rows: connectorIntegrations
        .filter(filterIntegrationBySearch)
        .filter(filterIntegrations)
        .filter((integration) => !!integration)
        .map((integration) => ({
          ...integration,
          icon: (
            <SoftBox
              component="img"
              width="100%"
              src={integration.icon ? integration.icon : defaultIntegrationIcon}
            />
          ),
          created_date_time:
            formatDateAndTime(getLocalDateTimeString(integration.created_date_time)) || "",
          integration,
        })),
    };
  }, [
    integrations,
    connectorIntegrationIds,
    searchName,
    sharedIntegrations,
    userPermissions,
    user?.cxfUserId
  ]);

  useEffect(() => {
    const fetchConnectorIntegrationIds = async () => {
      if (!user?.stripeId || !connector?.id) {
        setConnectorIntegrationIds([]);
        return;
      }

      try {
        const dependenciesResponse = await getResourceDependenciesWithHop({
          tenantId: user.stripeId,
          sourceId: connector.id,
          direction: "reverse",
          directTargetResourceType: "integration",
          throughTargetResourceType: "flow",
          throughDirection: "forward",
          finalTargetResourceType: "integration",
          targetIdFromContextId: false
        });

        const integrationIds = Array.isArray(dependenciesResponse?.resultObject)
          ? dependenciesResponse.resultObject.map(
              (dependency) => dependency.target_id
            )
          : [];

        setConnectorIntegrationIds([...new Set(integrationIds)]);
      } catch (error) {
        console.error(error);
        setConnectorIntegrationIds([]);
      }
    };

    fetchConnectorIntegrationIds();
  }, [user?.stripeId, connector?.id]);

  return (
    <>
      <SoftBox>
        <SoftBox
          sx={{
            alignItems: "flex-start",
            display: "flex",
            justifyContent: "flex-start",
            padding: "12px 0 12px 40px",
          }}
        >
          <SoftInput
            size="medium"
            placeholder="Search..."
            value={searchName}
            onChange={({ currentTarget }) => {
              setSearchName(currentTarget.value);
            }}
            sx={{
              maxWidth: "380px",
              marginRight: "34px",
            }}
          />
        </SoftBox>
        <DataTable
          table={tableData}
          entriesPerPage={{ defaultValue: 25 }}
          hiddenTopButtons={true}
          noTopPart
          tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))"
        />
      </SoftBox>
      {!!selectedIntegration && (
        <UpdateIntegrationDialog
          open={showUpdateIntegrationDialog}
          onClose={handleCloseIntegrationDialog}
          integration={selectedIntegration}
        />
      )}
    </>
  );
};
